// CATALOGO DE LAYOUTS — a lista fechada de formas de arquivo que a Atlas fala.
//
// O padrao Atlas continua sendo o UNICO modelo interno: validacao, casamento,
// conciliacao e margem so' conhecem `RetornoBruto` e `RemessaEntrada`. O que
// este catalogo acrescenta e' a possibilidade de TRADUZIR na borda, para o ente
// cuja folha nao consegue falar o padrao. Ver ADR-0007.
//
// POR QUE E' CONSTANTE DE CODIGO, E NAO TABELA: incluir um layout novo exige
// escrever um tradutor, um teste e uma secao de documento — trabalho de
// engenharia, nao configuracao (FR-007). O que a averbadora escolhe por unidade
// e' QUAL destes vale, e isso sim e' dado (`admin_layout_atribuicoes`).
//
// FONTE UNICA: as `colunas` de cada arquivo sao os MESMOS arrays que o parser e
// o serializador usam. O documento tecnico sai daqui tambem — e' o que impede o
// papel descrever coluna diferente da que o codigo le, defeito que o cliente
// reportou em 24/07/2026.
import { validarLayoutColunas } from "../csv-delimitado.js";
import { ARQUIVO_CARGA, ARQUIVO_CONSIGNADOS, COLUNAS_CARGA, COLUNAS_CONSIGNADOS, RETORNO_LAYOUT_VERSAO, RETORNO_SEPARADOR, } from "../retorno.js";
import { ARQUIVO_RUBRICAS, COLUNAS_RUBRICA } from "../rubrica.js";
import { COLUNAS_MOVIMENTO, MOVIMENTO_ENCODING, MOVIMENTO_LAYOUT_VERSAO, MOVIMENTO_SEPARADOR, } from "../movimento-layout.js";
import { ALECE_LAYOUT_VERSAO, ALECE_MOVIMENTO_LAYOUT_VERSAO, ALECE_SEPARADOR_MOVIMENTO, COLUNAS_ALECE_FOLHA, COLUNAS_ALECE_MOVIMENTO, COLUNAS_ALECE_SERVIDOR, } from "./alece/alece-colunas.js";
import { COLUNAS_CONTRATOS_ALECE } from "./alece/alece-carga-colunas.js";
import { validarCenarios } from "./matriz-cenarios.js";
import { ASSALCE_ENTRADA_VERSAO, COLUNAS_ASSALCE } from "./assalce/assalce-colunas.js";
import { COLUNAS_TESTE_POSICIONAL, LARGURA_LINHA_POSICIONAL, TESTE_POSICIONAL_VERSAO, } from "./teste/posicional-colunas.js";
import { ABA_DESCONTOS, ABA_HOLERITE, ABA_MOVIMENTO, ABA_SERVIDORES, COLUNAS_TESTE_DESCONTOS, COLUNAS_TESTE_HOLERITE, COLUNAS_TESTE_MOVIMENTO, COLUNAS_TESTE_SERVIDORES, TESTE_PLANILHA_VERSAO, } from "./teste/planilha-colunas.js";
export const LAYOUT_CODIGOS = [
    "atlas-retorno",
    "atlas-movimento",
    "alece-retorno",
    "alece-carga-inicial",
    "alece-movimento",
    // Arquivo mensal de consignataria que nao e' banco (#165, ADR-0013).
    "assalce-entrada",
    // O retorno a consignataria (#189): o que a folha do ente fez nas rubricas
    // dela. Saida com finalidade propria — nao e' movimento e nao e' atribuivel.
    // O detalhado (sete colunas) e' o padrao; a ASSALCE tem o dela, de quatro.
    "consignataria-retorno",
    "assalce-retorno",
    // Sinteticos (feature 008, D-06): existem so' para dar fixture aos valores da
    // matriz que nenhum ente real cobre. `situacao: "teste"` os tira de toda
    // listagem e de `layoutsAtribuiveis`; ver `layouts/teste/`.
    "teste-posicional-retorno",
    "teste-planilha-retorno",
    "teste-planilha-movimento",
];
/**
 * O PADRAO ATLAS AINDA NAO TEM PROVA EM HOMOLOG REGISTRADA NO CODIGO.
 *
 * `validarCatalogo` exige `provaHomolog` para todo layout `disponivel`
 * (FR-032). O padrao ja esta disponivel desde antes de a prova existir; este
 * flag e' a UNICA excecao, e e' explicito de proposito para ser visto. T090
 * (specs/008-hub-layouts/tasks.md) roda a prova em homolog, copia a
 * referencia para `atlas-*` e vira isto para `false` — a partir dai o padrao e'
 * cobrado como qualquer outro.
 *
 * E' um flag, e nao uma data, porque a Camada 1 e' gate de commit do
 * repositorio inteiro: uma falha por data travaria toda feature no dia em que
 * vencesse.
 */
export const PROVA_PADRAO_PENDENTE = true;
/**
 * OS LAYOUTS DA ALECE ESTAO `disponivel` SEM PROVA EM HOMOLOG REGISTRADA.
 *
 * Mesma excecao do padrao, pelo mesmo motivo de forma e por um motivo de fundo
 * diferente: aqui a operacao ja depende deles. Enquanto os tres eram
 * `rascunho`, a ALECE REAL nao podia recebe-los — em homolog e producao
 * rascunho so' vai para unidade de prova (`layout-atribuicao-store.ts`) — e a
 * unidade ficava no padrao Atlas, que nao le o formato dela.
 *
 * O QUE ISTO NAO DIZ: que a prova passou. Ela nao passou. Em 15/09/2026 a prova
 * de ponta a ponta nao fechava verde para layout NENHUM, o padrao incluido, e
 * tres defeitos dela foram corrigidos nesse dia (nome `-PREVIA` decidido por
 * calendario, distribuicao pendente travando a folha, 404 por cache de isolate
 * em `ensureTrocaArquivosLoaded`). O que restou esta em PENDENCIAS.md: `F4a`
 * emite o movimento depois de `F2` aplicar o retorno, e nenhuma ordem dentro de
 * uma competencia satisfaz as dependencias dos dois.
 *
 * O QUE FECHA ISTO: rodar `pnpm --filter @atlas/api test:e2e:layouts --
 * --layout alece-retorno` contra homolog, copiar o `id` verde para
 * `provaHomolog` nas tres definicoes e virar este flag para `false` — a partir
 * dai a ALECE e' cobrada como qualquer outro ente (roteiro, passo 10).
 *
 * E' um flag, e nao uma data, pela mesma razao do padrao: a Camada 1 e' gate de
 * commit do repositorio inteiro, e uma falha por data travaria toda feature no
 * dia em que vencesse.
 */
export const PROVA_ALECE_PENDENTE = true;
const ARQUIVOS_ATLAS_RETORNO = [
    {
        papel: ARQUIVO_CARGA,
        nomePadrao: "carga{AAAAMM}.csv",
        reconhecidoPor: ARQUIVO_CARGA,
        obrigatorio: true,
        cabecalho: false,
        separador: RETORNO_SEPARADOR,
        codificacao: "detectar",
        colunas: COLUNAS_CARGA,
    },
    {
        papel: ARQUIVO_CONSIGNADOS,
        nomePadrao: "retorno-consignados.csv",
        reconhecidoPor: ARQUIVO_CONSIGNADOS,
        obrigatorio: true,
        cabecalho: false,
        separador: RETORNO_SEPARADOR,
        codificacao: "detectar",
        colunas: COLUNAS_CONSIGNADOS,
    },
    {
        papel: ARQUIVO_RUBRICAS,
        nomePadrao: "rubrica_funcionarios{AAAAMM}.csv",
        reconhecidoPor: ARQUIVO_RUBRICAS,
        obrigatorio: true,
        cabecalho: false,
        separador: RETORNO_SEPARADOR,
        codificacao: "detectar",
        colunas: COLUNAS_RUBRICA,
    },
];
const ARQUIVOS_ATLAS_MOVIMENTO = [
    {
        papel: "movimento",
        nomePadrao: "movimento_{unidade}_{AAAAMM}.csv",
        reconhecidoPor: "movimento",
        obrigatorio: true,
        cabecalho: false,
        separador: MOVIMENTO_SEPARADOR,
        codificacao: MOVIMENTO_ENCODING,
        colunas: COLUNAS_MOVIMENTO,
    },
];
const ARQUIVOS_ALECE_RETORNO = [
    {
        papel: "servidor",
        nomePadrao: "ALECE-SERVIDOR-{AAMM}.TXT",
        reconhecidoPor: "servidor",
        obrigatorio: true,
        cabecalho: true,
        separador: "detectar",
        codificacao: "detectar",
        colunas: COLUNAS_ALECE_SERVIDOR,
    },
    {
        papel: "folha",
        nomePadrao: "ALECE-FOLHA-{AAMM}.TXT",
        reconhecidoPor: "folha",
        obrigatorio: true,
        cabecalho: true,
        separador: "detectar",
        codificacao: "detectar",
        colunas: COLUNAS_ALECE_FOLHA,
    },
];
const ARQUIVOS_ALECE_MOVIMENTO = [
    {
        papel: "movimento",
        nomePadrao: "ALECE-MOVIMENTO-{AAMM}.TXT",
        reconhecidoPor: "movimento",
        obrigatorio: true,
        cabecalho: true,
        // `;` e decimal em virgula desde 23/09/2026 (1.3). Os arquivos que a ALECE
        // manda continuam com virgula.
        separador: ALECE_SEPARADOR_MOVIMENTO,
        codificacao: "latin1",
        colunas: COLUNAS_ALECE_MOVIMENTO,
    },
];
// A CARGA INICIAL DECLARA SERVIDOR E FOLHA POR CONTA PROPRIA, e nao por
// `...ARQUIVOS_ALECE_RETORNO`.
//
// O conteudo dos dois e' o mesmo do retorno mensal — as MESMAS colunas, a MESMA
// virgula — e por isso o spread parecia economia. Ele nao era: o que muda entre
// os dois e' a EXTENSAO (`.CSV` na carga, `.TXT` no mensal), e a extensao mora
// exatamente no campo que o spread copiava. Enquanto herdou, a ficha do layout
// prometia `.TXT` ao ente e o adaptador recusava o que a ficha mandou enviar.
//
// As colunas continuam compartilhadas de proposito: elas SAO as mesmas, e
// duplica-las criaria duas verdades sobre o dicionario do arquivo.
const ARQUIVOS_ALECE_CARGA_INICIAL = [
    {
        papel: "servidor",
        nomePadrao: "ALECE-SERVIDOR-{AAMM}.CSV",
        reconhecidoPor: "servidor",
        obrigatorio: true,
        cabecalho: true,
        separador: "detectar",
        codificacao: "detectar",
        colunas: COLUNAS_ALECE_SERVIDOR,
    },
    {
        papel: "folha",
        nomePadrao: "ALECE-FOLHA-{AAMM}.CSV",
        reconhecidoPor: "folha",
        obrigatorio: true,
        cabecalho: true,
        separador: "detectar",
        codificacao: "detectar",
        colunas: COLUNAS_ALECE_FOLHA,
    },
    {
        papel: "contratos",
        nomePadrao: "ALECE-CONTRATOS-{AAMM}.xlsx",
        reconhecidoPor: "contratos",
        obrigatorio: true,
        cabecalho: true,
        separador: "",
        codificacao: "utf-8",
        colunas: COLUNAS_CONTRATOS_ALECE.map((nome, i) => ({
            nome,
            coluna: i + 1,
            kind: "alfa",
            obrigatorio: true,
        })),
    },
];
// ---------------------------------------------------------- layouts de teste
//
// `separador: ""` nos tres: largura fixa nao tem separador, e celula de
// planilha tambem nao — a coluna e' a propria celula. `reconhecidoPor` traz o
// trecho do nome padrao para o documento nao ficar mudo, mas os DOIS layouts de
// entrada reconhecem pelo CONTEUDO (`nome-arquivo: reconhecido-por-conteudo`);
// e' o adaptador quem manda, e o nome aqui e' ilustracao.
const ARQUIVOS_ASSALCE_ENTRADA = [
    {
        papel: "consignacoes",
        nomePadrao: "ASS{AAAAMM}.TXT",
        reconhecidoPor: "ASS",
        obrigatorio: true,
        cabecalho: false,
        separador: "",
        codificacao: "detectar",
        colunas: COLUNAS_ASSALCE,
    },
];
const ARQUIVOS_TESTE_POSICIONAL = [
    {
        papel: "retorno",
        nomePadrao: "POSICIONAL-{AAMM}.TXT",
        reconhecidoPor: "posicional",
        obrigatorio: true,
        cabecalho: false,
        separador: "",
        codificacao: "latin1",
        colunas: COLUNAS_TESTE_POSICIONAL,
    },
];
const ARQUIVOS_TESTE_PLANILHA_RETORNO = [
    {
        papel: "servidores",
        nomePadrao: `${ABA_SERVIDORES} (aba)`,
        reconhecidoPor: ABA_SERVIDORES,
        obrigatorio: true,
        cabecalho: true,
        separador: "",
        codificacao: "detectar",
        colunas: COLUNAS_TESTE_SERVIDORES,
    },
    {
        papel: "descontos",
        nomePadrao: `${ABA_DESCONTOS} (aba)`,
        reconhecidoPor: ABA_DESCONTOS,
        obrigatorio: true,
        cabecalho: true,
        separador: "",
        codificacao: "detectar",
        colunas: COLUNAS_TESTE_DESCONTOS,
    },
    {
        papel: "holerite",
        nomePadrao: `${ABA_HOLERITE} (aba)`,
        reconhecidoPor: ABA_HOLERITE,
        obrigatorio: true,
        cabecalho: true,
        separador: "",
        codificacao: "detectar",
        colunas: COLUNAS_TESTE_HOLERITE,
    },
];
const ARQUIVOS_TESTE_PLANILHA_MOVIMENTO = [
    {
        papel: "movimento",
        nomePadrao: "MOVIMENTO-{AAAAMM}-{PREVIA|DEFINITIVO}.xlsx",
        reconhecidoPor: ABA_MOVIMENTO,
        obrigatorio: true,
        cabecalho: true,
        separador: "",
        codificacao: "utf-8",
        colunas: COLUNAS_TESTE_MOVIMENTO,
    },
];
export const CATALOGO_LAYOUTS = [
    {
        codigo: "atlas-retorno",
        nome: "Padrão Atlas — retorno",
        versao: RETORNO_LAYOUT_VERSAO,
        sentido: "entrada",
        descricao: `ZIP com carga, consignados e rubrica, CSV "${RETORNO_SEPARADOR}" sem cabeçalho`,
        arquivos: ARQUIVOS_ATLAS_RETORNO,
        situacao: "disponivel",
        limitacoes: [],
        camposDerivados: [],
        aliases: ["retorno-pacote", "retorno"],
        // O que o parser do padrao FAZ hoje: zip ou os tres CSVs soltos (corpo
        // JSON), ponto e virgula com aspas opcionais, sem cabecalho, BOM tolerado,
        // qualquer fim de linha, dinheiro com virgula ou ponto e milhar.
        cenarios: {
            empacotamento: ["pacote-comprimido", "soltos-juntos"],
            delimitacao: ["ponto-virgula", "aspas-opcionais"],
            cabecalho: ["posicional-sem-cabecalho"],
            "tipo-registro": ["nenhum"],
            "codificacao-fim-de-linha": ["utf-8", "utf-8-bom", "latin1", "crlf", "lf", "linhas-vazias-no-fim"],
            numeros: ["decimal-virgula", "decimal-ponto", "separador-de-milhar", "zeros-a-esquerda"],
            "datas-competencia": ["ddmmaaaa", "dd-mm-aaaa", "aaaa-mm-dd", "competencia-aaaamm"],
            identificadores: ["cpf-11-digitos", "cpf-com-mascara", "banco-3-digitos"],
            granularidade: ["linha-por-contrato"],
            "campos-ausentes": ["nenhum"],
            "nome-arquivo": ["padrao-por-competencia", "reconhecido-por-nome"],
            sentido: ["entrada"],
            volume: ["volume-real-conhecido"],
        },
        pendencias: [],
        exemplo: { descricao: "Os três CSVs do pacote de exemplo da carga inicial, competência 202608." },
    },
    {
        codigo: "atlas-movimento",
        nome: "Padrão Atlas — movimento",
        versao: MOVIMENTO_LAYOUT_VERSAO,
        sentido: "saida",
        descricao: `CSV "${MOVIMENTO_SEPARADOR}" de largura fixa, ${MOVIMENTO_ENCODING}, sem cabeçalho`,
        arquivos: ARQUIVOS_ATLAS_MOVIMENTO,
        situacao: "disponivel",
        limitacoes: [],
        camposDerivados: [],
        aliases: ["movimento", "remessa"],
        // Barra vertical com campos de largura fixa (os dois ao mesmo tempo), latin1,
        // CPF escrito com mascara, competencia AAAAMM na ultima coluna.
        cenarios: {
            empacotamento: ["solto"],
            delimitacao: ["barra-vertical", "largura-fixa", "sem-aspas"],
            cabecalho: ["posicional-sem-cabecalho"],
            "tipo-registro": ["nenhum"],
            "codificacao-fim-de-linha": ["latin1", "crlf"],
            numeros: ["decimal-virgula", "zeros-a-esquerda"],
            "datas-competencia": ["competencia-aaaamm"],
            identificadores: ["cpf-com-mascara"],
            granularidade: ["linha-por-contrato"],
            "campos-ausentes": ["nenhum"],
            "nome-arquivo": ["padrao-por-competencia", "marca-previa-definitivo"],
            sentido: ["saida"],
            volume: ["volume-real-conhecido"],
        },
        pendencias: [],
        exemplo: { descricao: "O movimento de duas dívidas (uma da Atlas, uma herdada) da competência 202608." },
        // Sem divida nenhuma, `serializarMovimento` devolve texto vazio — e e' assim
        // que sempre foi (T010 conferiu). Declarar outra coisa mudaria o byte a byte.
        casoVazio: "arquivo-vazio",
    },
    {
        codigo: "alece-retorno",
        nome: "ALECE — base de servidores e extrato de descontos",
        versao: ALECE_LAYOUT_VERSAO,
        sentido: "entrada",
        descricao: "Base de servidores e extrato de descontos; separador detectado pelo cabeçalho: ponto e vírgula, vírgula ou tabulação",
        arquivos: ARQUIVOS_ALECE_RETORNO,
        situacao: "disponivel",
        limitacoes: [
            "Não traz número do contrato: o casamento cai no fallback por matrícula, verba e valor.",
            "Não traz parcela atual nem total: o cronograma das dívidas herdadas não é atualizado.",
            "Uma linha pode representar mais de um contrato do mesmo credor (`N CONTRATO(S)`).",
            "Não traz situação funcional: desligamento é derivado da ausência na base somada ao motivo do desconto.",
            "Não traz proventos nem rubricas obrigatórias: o holerite fica incompleto.",
        ],
        camposDerivados: [
            { campo: "situacao", regra: "Presente na base do servidor → `AT`. Nunca `AP` a partir do regime." },
            {
                campo: "situacao",
                regra: "Ausente da base e presente no extrato com motivo FALECIDO ou DESLIGADO, e conhecida da Atlas → `DE`. " +
                    "Ausente sem motivo não gera linha.",
            },
            {
                campo: "cpf",
                regra: "Completado com zeros à esquerda até 11 dígitos e conferido pelo dígito verificador.",
            },
            {
                campo: "salarioLiquido",
                regra: "`SALBASE − DESCOBRIG` — a base que a folha usa para calcular margem.",
            },
            { campo: "bancoCodigo", regra: "Completado com zeros à esquerda até 3 dígitos." },
        ],
        cenarios: {
            empacotamento: ["soltos-juntos", "pacote-comprimido"],
            delimitacao: ["ponto-virgula", "virgula", "tabulacao", "aspas-opcionais"],
            cabecalho: ["nome-de-coluna-ordem-livre"],
            "tipo-registro": ["nenhum"],
            "codificacao-fim-de-linha": ["latin1", "utf-8", "utf-8-bom", "crlf", "lf", "linhas-vazias-no-fim"],
            numeros: ["decimal-virgula"],
            "datas-competencia": ["dd-mm-aaaa", "competencia-aaaamm"],
            identificadores: ["cpf-sem-zeros", "cpf-11-digitos", "banco-3-digitos", "matricula-com-zeros"],
            granularidade: ["somado-por-matricula-verba"],
            "campos-ausentes": [
                "sem-identificador-da-divida",
                "sem-parcela-e-total",
                "sem-situacao-funcional-derivada",
            ],
            "nome-arquivo": ["padrao-por-competencia", "reconhecido-por-nome"],
            sentido: ["entrada"],
            volume: ["volume-real-conhecido"],
        },
        // As cinco perguntas do relatorio tecnico de 02/09/2026 foram respondidas
        // pela ALECE e fechadas em 14/09/2026 (passo 7 do roteiro: pendencia fecha
        // por REMOCAO). O que falta para `disponivel` e' o passo 9 — a prova de
        // ponta a ponta em homolog, cuja referencia entra em `provaHomolog`.
        pendencias: [],
        exemplo: {
            descricao: "Base de servidores e extrato de descontos sintéticos da competência 202609, gerados do dicionário da ALECE.",
        },
    },
    {
        codigo: "alece-carga-inicial",
        nome: "ALECE — carga inicial completa",
        // 1.4: delimitador detectado pelo cabeçalho e recusa de perda total.
        versao: "1.4",
        sentido: "entrada",
        finalidade: "carga_inicial",
        descricao: "Tombamento com servidores e folha delimitados por ponto e vírgula, vírgula ou tabulação (detecção pelo cabeçalho), e planilha detalhada de contratos",
        arquivos: ARQUIVOS_ALECE_CARGA_INICIAL,
        situacao: "disponivel",
        limitacoes: ["Uso exclusivo no onboarding; não substitui o retorno mensal."],
        camposDerivados: [
            { campo: "credor", regra: "Todo contrato nasce com o credor sentinela 0 (Credor a identificar)." },
            { campo: "parcelasPagas", regra: "Prazo Total menos Prazo Restante." },
            {
                campo: "naturezaRubrica",
                regra: "Mensalidade, plano de saúde, plano odontológico, seguros, adiantamento, compras diversas e diferenças são descontos não consignados.",
            },
        ],
        cenarios: {
            empacotamento: ["pacote-comprimido", "planilha-por-arquivo"],
            delimitacao: ["ponto-virgula", "virgula", "tabulacao", "celulas-planilha"],
            cabecalho: ["nome-de-coluna-ordem-livre"],
            "tipo-registro": ["nenhum"],
            // `utf-8-bom` entrou em 16/09/2026: os CSV que a ALECE exporta trazem a
            // marca, e ate aqui o layout nao a declarava. `latin1` FICA, e a escolha
            // e' deliberada — `codificacaoDoLayout` (apps/api, layout-servico.ts) so'
            // obedece a declaracao quando ela e' inequivoca, e latin1+utf-8 mantem a
            // deteccao byte a byte. Tirar `latin1` para "declarar UTF-8" forcaria a
            // decodificacao e quebraria o export antigo que ainda chega em latin1.
            "codificacao-fim-de-linha": ["latin1", "utf-8", "utf-8-bom", "crlf", "lf"],
            numeros: ["decimal-virgula", "decimal-ponto"],
            "datas-competencia": ["dd-mm-aaaa", "competencia-aaaamm"],
            identificadores: ["cpf-sem-zeros", "cpf-com-mascara", "cpf-11-digitos", "matricula-com-zeros"],
            granularidade: ["linha-por-contrato", "somado-por-matricula-verba"],
            "campos-ausentes": ["nenhum"],
            "nome-arquivo": ["padrao-por-competencia", "reconhecido-por-nome"],
            sentido: ["entrada"],
            volume: ["volume-real-conhecido"],
        },
        pendencias: [],
        exemplo: { descricao: "Servidor, folha agregada e contrato legado sintético da competência 202609." },
    },
    {
        codigo: "alece-movimento",
        nome: "ALECE — movimento na estrutura do extrato de descontos",
        versao: ALECE_MOVIMENTO_LAYOUT_VERSAO,
        sentido: "saida",
        descricao: "Arquivo delimitado por ponto e vírgula, com cabeçalho, nas nove colunas do extrato da ALECE e valores com vírgula decimal",
        arquivos: ARQUIVOS_ALECE_MOVIMENTO,
        situacao: "disponivel",
        limitacoes: [
            "Não tem coluna de tipo de movimento: só a estratégia de encerramento por omissão é suportada.",
            "No modo agregado, dívidas do mesmo servidor na mesma verba saem somadas e a ADF não vai no arquivo.",
        ],
        camposDerivados: [
            { campo: "FF", regra: "Folha funcional do servidor, do arquivo SERVIDOR do retorno: a do compromisso do contrato, senão a da foto de margem do vínculo; o cadastro é o último recurso." },
            { campo: "DESCRUB", regra: "Descrição da verba no catálogo de rubricas da unidade; sem ela, o nome do credor conhecido. Credor a identificar nunca vai para o arquivo: a célula sai vazia e o relatório acusa DESCRUB." },
            { campo: "OBS", regra: "Sempre a quantidade de contratos da linha: `1 CONTRATO(S)`, `2 CONTRATO(S)`… Nunca a ADF." },
        ],
        cenarios: {
            empacotamento: ["solto"],
            delimitacao: ["virgula", "aspas-opcionais"],
            cabecalho: ["nome-de-coluna-ordem-livre"],
            "tipo-registro": ["nenhum"],
            "codificacao-fim-de-linha": ["latin1", "crlf"],
            numeros: ["decimal-virgula"],
            "datas-competencia": ["dd-mm-aaaa", "competencia-aaaamm"],
            identificadores: ["cpf-11-digitos"],
            granularidade: ["somado-por-matricula-verba", "linha-por-contrato"],
            "campos-ausentes": ["sem-identificador-da-divida"],
            "nome-arquivo": ["padrao-por-competencia", "marca-previa-definitivo"],
            sentido: ["saida"],
            volume: ["volume-real-conhecido"],
        },
        pendencias: [],
        exemplo: { descricao: "O movimento de exemplo escrito na estrutura do extrato da ALECE, modo agregado." },
        casoVazio: "so-cabecalho",
    },
    // ======================================================== retorno a consignataria
    {
        // O PADRAO de quem nao tem formato proprio: todo credor sem `retornoLayout`
        // e as rubricas sem consignataria vinculada. A 1.0 era a 2.0 do
        // `assalce-retorno` (30/09/2026) com outro nome; a 2.0 (01/10/2026) poe o
        // NOME do servidor depois do CPF e o nome do credor no nome do arquivo.
        codigo: "consignataria-retorno",
        nome: "Retorno à consignatária — detalhado por contrato",
        versao: "2.0",
        sentido: "saida",
        finalidade: "retorno_consignataria",
        descricao: "TXT sem cabeçalho, separado por ponto e vírgula: matrícula, CPF, nome, verba, contrato, valor instruído, valor descontado e motivo, uma linha por contrato de cada linha do retorno do órgão/entidade nas rubricas da consignatária, inclusive as sem desconto",
        arquivos: [
            {
                papel: "retorno",
                nomePadrao: "RE{CREDOR}{AAAAMM}.txt",
                reconhecidoPor: "re",
                obrigatorio: true,
                cabecalho: false,
                separador: ";",
                codificacao: "utf-8",
                colunas: [
                    { nome: "MATRICULA", coluna: 1, kind: "alfa", obrigatorio: true, descricao: "Matrícula como o órgão/entidade a escreve no retorno (com os zeros à esquerda que ele põe)." },
                    { nome: "CPF", coluna: 2, kind: "alfa", obrigatorio: true, descricao: "CPF do servidor, 11 dígitos, sem máscara." },
                    { nome: "NOME", coluna: 3, kind: "alfa", obrigatorio: false, descricao: "Nome do servidor como está no cadastro da Atlas, sem acento e sem ponto e vírgula. Vazio quando a matrícula não está cadastrada." },
                    { nome: "VERBA", coluna: 4, kind: "alfa", obrigatorio: true, descricao: "Código da rubrica como o órgão/entidade o escreve no retorno." },
                    { nome: "CONTRATO", coluna: 5, kind: "alfa", obrigatorio: false, descricao: "ADF do contrato de crédito na Atlas. `SEM ADF` na consignação não creditícia (plano, convênio), que é um desconto por matrícula e verba sem contrato. Vazio no crédito que a Atlas não tem como nomear: a linha da folha sai inteira." },
                    { nome: "VALOR_INSTRUIDO", coluna: 6, kind: "dinheiro", decimal: "virgula", obrigatorio: true, descricao: "O que foi instruído ao contrato: a parcela prevista, ou o VALOR da folha quando a linha sai inteira. Vírgula decimal, sem zeros à esquerda." },
                    { nome: "VALOR_DESCONTADO", coluna: 7, kind: "dinheiro", decimal: "virgula", obrigatorio: true, descricao: "O valor consignado: o que a folha descontou do contrato, 0,00 quando não descontou. Vírgula decimal, sem zeros à esquerda." },
                    { nome: "MOTIVO", coluna: 8, kind: "alfa", obrigatorio: false, descricao: "MOTIVO da folha (PGTO OK, MARGEM INSUFICIENTE, DESLIGADO, FALECIDO), sem acento e sem ponto e vírgula." },
                ],
            },
        ],
        // Nenhuma consignataria conferiu ainda um arquivo neste formato.
        situacao: "rascunho",
        limitacoes: [
            "Só consignação: linha TIPO N da folha (a contribuição associativa, por exemplo) não vai no arquivo.",
            "Não distingue folha funcional: a mesma pessoa em duas folhas sai em duas linhas iguais na matrícula, na verba e no contrato.",
            "A verba sai como o órgão/entidade a escreve no retorno, sem de-para para códigos próprios da consignatária.",
            "Contrato de crédito com desconto a ratear só se separa depois de a averbadora distribuir o grupo: com grupo pendente, o arquivo não é gerado.",
            "O nome vem do cadastro de servidores, não da folha: matrícula sem cadastro na Atlas sai com o nome vazio.",
        ],
        camposDerivados: [
            { campo: "NOME", regra: "Nome do servidor cadastrado na unidade com a mesma matrícula (sem os zeros à esquerda); a folha não traz nome." },
            { campo: "CONTRATO", regra: "ADF do contrato de crédito que a conciliação casou com a linha da folha; `SEM ADF` na consignação não creditícia." },
            { campo: "VALOR_INSTRUIDO", regra: "Parcela prevista do contrato no grupo de desconto; o VALOR da folha quando a linha sai inteira." },
            { campo: "VALOR_DESCONTADO", regra: "Valor pago da linha TIPO C do retorno, repartido entre os contratos como a conciliação distribuiu; nunca o valor averbado." },
        ],
        cenarios: {
            empacotamento: ["solto"],
            delimitacao: ["ponto-virgula", "sem-aspas"],
            cabecalho: ["posicional-sem-cabecalho"],
            "tipo-registro": ["nenhum"],
            "codificacao-fim-de-linha": ["utf-8", "crlf"],
            numeros: ["decimal-virgula"],
            "datas-competencia": ["competencia-aaaamm"],
            identificadores: ["cpf-11-digitos", "matricula-com-zeros"],
            granularidade: ["linha-por-contrato"],
            "campos-ausentes": ["sem-parcela-e-total"],
            "nome-arquivo": ["padrao-por-competencia"],
            sentido: ["saida"],
            volume: ["volume-real-conhecido"],
        },
        pendencias: [
            { descricao: "Uma consignatária conferir o primeiro arquivo gerado neste formato sobre um retorno real.", desde: "2026-10-01" },
        ],
        exemplo: { descricao: "Quatro linhas do retorno do órgão/entidade nas rubricas do Banco do Brasil viram cinco no REBRASIL: integral, parcial, desligado sem desconto e uma linha de dois contratos, de matrícula sem zeros." },
        casoVazio: "arquivo-vazio",
    },
    {
        codigo: "assalce-retorno",
        nome: "ASSALCE — retorno do que a folha da ALECE descontou de cada contrato",
        // 3.0 (01/10/2026): as quatro colunas que a ASSALCE pediu de volta, com o
        // resto da 2.0 — tudo do FOLHA, sem desconto com 0,00, um contrato por
        // linha. A 2.0 (sete colunas) virou o `consignataria-retorno`; a 1.0 so'
        // levava o que teve desconto, somado por matricula e verba.
        versao: "3.0",
        sentido: "saida",
        finalidade: "retorno_consignataria",
        descricao: "TXT sem cabeçalho, separado por ponto e vírgula: matrícula, CPF, verba e valor descontado, uma linha por contrato de cada linha do retorno da ALECE nas rubricas da ASSALCE, inclusive as sem desconto",
        arquivos: [
            {
                papel: "retorno",
                nomePadrao: "RETASS{AAAAMM}.txt",
                reconhecidoPor: "retass",
                obrigatorio: true,
                cabecalho: false,
                separador: ";",
                codificacao: "utf-8",
                colunas: [
                    { nome: "MATRICULA", coluna: 1, kind: "alfa", obrigatorio: true, descricao: "Matrícula como a ALECE a escreve no retorno (com os zeros à esquerda que ela põe)." },
                    { nome: "CPF", coluna: 2, kind: "alfa", obrigatorio: true, descricao: "CPF do servidor, 11 dígitos, sem máscara." },
                    { nome: "VERBA", coluna: 3, kind: "alfa", obrigatorio: true, descricao: "Código da rubrica como a ALECE o escreve no retorno." },
                    { nome: "VALOR_DESCONTADO", coluna: 4, kind: "dinheiro", decimal: "virgula", obrigatorio: true, descricao: "O que a folha descontou do contrato (PAGOFP), 0,00 quando não descontou. Vírgula decimal, sem zeros à esquerda." },
                ],
            },
        ],
        // A 1.0 foi confirmada pela ASSALCE em 29/09/2026 por mensagem; a 3.0 sai
        // de rascunho quando ela conferir o primeiro arquivo no formato novo.
        situacao: "rascunho",
        limitacoes: [
            "Só consignação: linha TIPO N da folha (a contribuição associativa, por exemplo) não vai no arquivo.",
            "Não identifica o contrato: dois contratos da mesma matrícula e verba saem em duas linhas iguais, menos no valor, na ordem da folha.",
            "Não diz o motivo: a linha sem desconto sai com 0,00, sem dizer se foi desligamento, falecimento ou margem insuficiente.",
            "Não distingue folha funcional: a mesma pessoa em duas folhas sai em linhas iguais na matrícula e na verba.",
            "A verba sai como a ALECE a escreve no retorno, sem de-para para códigos próprios da ASSALCE.",
            "Contrato de crédito com desconto a ratear só se separa depois de a averbadora distribuir o grupo: com grupo pendente, o arquivo não é gerado.",
        ],
        camposDerivados: [
            { campo: "VALOR_DESCONTADO", regra: "PAGOFP da linha TIPO C do retorno da ALECE, repartido entre os contratos como a conciliação distribuiu; nunca somado e nunca o valor averbado." },
        ],
        cenarios: {
            empacotamento: ["solto"],
            delimitacao: ["ponto-virgula", "sem-aspas"],
            cabecalho: ["posicional-sem-cabecalho"],
            "tipo-registro": ["nenhum"],
            "codificacao-fim-de-linha": ["utf-8", "crlf"],
            numeros: ["decimal-virgula"],
            "datas-competencia": ["competencia-aaaamm"],
            identificadores: ["cpf-11-digitos", "matricula-com-zeros"],
            granularidade: ["linha-por-contrato"],
            "campos-ausentes": ["sem-identificador-da-divida", "sem-parcela-e-total", "sem-nome"],
            "nome-arquivo": ["padrao-por-competencia"],
            sentido: ["saida"],
            volume: ["volume-real-conhecido"],
        },
        pendencias: [
            { descricao: "Combinar com a ASSALCE o canal e o prazo de entrega do arquivo (hoje: download na plataforma).", desde: "2026-09-29" },
            { descricao: "Avisar a ASSALCE da versão 3.0 (quatro colunas, linhas sem desconto com 0,00, um contrato por linha) e ela conferir o primeiro arquivo.", desde: "2026-10-01" },
        ],
        exemplo: { descricao: "Quatro linhas do retorno da ALECE nas rubricas da ASSALCE viram cinco no arquivo: integral, parcial, desligado sem desconto (0,00) e uma linha de dois contratos, cada um com o seu valor." },
        casoVazio: "arquivo-vazio",
    },
    // ======================================================== layouts de teste
    //
    // Sinteticos (D-06). Nao ha ente por tras de nenhum deles, e nenhum sai do
    // vitest: `situacao: "teste"` e' filtrada em `layoutsVisiveis`,
    // `layoutsAtribuiveis`, no hub, na ficha e no documento da prefeitura. Eles
    // existem porque FR-018/019 exigem que TODO valor da matriz tenha um exemplo
    // que o exercite — e os valores abaixo nenhum ente real cobre hoje.
    //
    // `pendencias` vazio e sem `provaHomolog` nao e' descuido: `validarCatalogo`
    // EXIGE os dois assim para `teste`. Prova de ponta a ponta e' contra ambiente
    // real, e nao ha ambiente real para um layout que nao e' de ninguem.
    // O ARQUIVO MENSAL DA ASSALCE (#165). Rascunho: as pendencias abaixo sao
    // respostas que so' a ASSALCE da'. Enquanto isso, so' a unidade de prova o
    // recebe (FR-035).
    {
        codigo: "assalce-entrada",
        nome: "ASSALCE — arquivo mensal de averbações",
        versao: ASSALCE_ENTRADA_VERSAO,
        sentido: "entrada",
        finalidade: "entrada_consignataria",
        descricao: "Arquivo de largura fixa (38 posições), sem cabeçalho, em que a ASSALCE informa o valor do mês de cada associado em cada rubrica que ela responde na ALECE",
        arquivos: ARQUIVOS_ASSALCE_ENTRADA,
        situacao: "rascunho",
        limitacoes: [
            "Não traz o CPF: ele vem do cadastro da ALECE, pela matrícula.",
            "Não traz o nome do associado.",
            "Não identifica a averbação: é uma linha por matrícula e código, com o valor somado quando o associado tem mais de uma.",
        ],
        camposDerivados: [
            { campo: "competencia", regra: "Do nome do arquivo: ASS{AAAAMM}.TXT." },
            { campo: "matricula", regra: "Zeros à esquerda removidos (001476 vira 1476), para casar com o cadastro; o valor original é mantido." },
            { campo: "folha", regra: "Zero à esquerda removido (03 vira 3); o valor original é mantido." },
            { campo: "referencia", regra: "MM/AA a MM/AA lido como competência inicial e final, com o século 20." },
        ],
        cenarios: {
            empacotamento: ["solto"],
            delimitacao: ["largura-fixa"],
            cabecalho: ["posicional-sem-cabecalho"],
            "tipo-registro": ["nenhum"],
            "codificacao-fim-de-linha": ["utf-8", "crlf", "linhas-vazias-no-fim"],
            numeros: ["decimal-virgula", "zeros-a-esquerda"],
            "datas-competencia": ["competencia-aaaamm", "intervalo-mm-aa"],
            identificadores: ["matricula-zeros-a-esquerda"],
            granularidade: ["somado-por-matricula-verba"],
            "campos-ausentes": ["sem-identificador-da-divida", "sem-nome", "sem-cpf"],
            "nome-arquivo": ["padrao-por-competencia"],
            sentido: ["entrada"],
            volume: ["volume-real-conhecido"],
        },
        pendencias: [
            { descricao: "Confirmar com a ASSALCE que o arquivo é sempre o retrato completo do mês (é a regra que encerra plano de saúde por omissão).", desde: "2026-09-24" },
            { descricao: "A ASSALCE passar a entregar .txt ou .zip, e não .rar.", desde: "2026-09-24" },
            { descricao: "Combinar a data de entrega antes do corte da ALECE, e o canal.", desde: "2026-09-24" },
            { descricao: "Receber o ASS202608, para a prova contra a folha da ALECE de agosto.", desde: "2026-09-24" },
            { descricao: "Saber o que a ASSALCE quer receber de volta por linha (descontado, parcial, recusado).", desde: "2026-09-24" },
        ],
        exemplo: {
            descricao: "Um ASS202609.TXT sintético com plano, avulsa, averbação com prazo (referência), valor zero, linha torta e linhas vazias no fim.",
        },
    },
    {
        codigo: "teste-posicional-retorno",
        nome: "Teste — retorno posicional com tipo de registro",
        versao: TESTE_POSICIONAL_VERSAO,
        sentido: "entrada",
        descricao: `Arquivo de largura fixa (${LARGURA_LINHA_POSICIONAL} posições), Latin-1, com abertura, detalhes e fechamento conferido`,
        arquivos: ARQUIVOS_TESTE_POSICIONAL,
        situacao: "teste",
        limitacoes: [
            "Não traz o nome do servidor: a carga recebe `SERVIDOR <matrícula>`, derivado da própria matrícula.",
            "Não é atribuível a unidade nenhuma — é um layout sintético, e existe só para a suíte de testes.",
        ],
        camposDerivados: [
            {
                campo: "nome",
                regra: "`SERVIDOR <matrícula>` — o registro de servidor não traz nome, e a carga exige um.",
            },
            { campo: "competencia", regra: "AAMM da linha de abertura, com o século 20 assumido." },
        ],
        cenarios: {
            empacotamento: ["solto"],
            delimitacao: ["largura-fixa"],
            cabecalho: ["posicional-sem-cabecalho"],
            "tipo-registro": ["abertura-detalhe-fechamento", "fechamento-com-totais"],
            "codificacao-fim-de-linha": ["latin1", "crlf", "linhas-vazias-no-fim"],
            numeros: ["centavos-implicitos", "zeros-a-esquerda", "com-sinal"],
            "datas-competencia": ["ddmmaaaa", "competencia-aamm"],
            identificadores: ["cpf-11-digitos", "matricula-com-digito"],
            granularidade: ["linha-por-contrato"],
            "campos-ausentes": ["sem-nome"],
            "nome-arquivo": ["reconhecido-por-conteudo"],
            sentido: ["entrada"],
            volume: ["volume-real-conhecido"],
        },
        pendencias: [],
        exemplo: {
            descricao: "Um arquivo posicional Latin-1 com abertura, quatro servidores, cinco descontos, fechamento conferido e duas linhas vazias no fim.",
        },
    },
    {
        codigo: "teste-planilha-retorno",
        nome: "Teste — retorno em planilha com títulos e identificadores tortos",
        versao: TESTE_PLANILHA_VERSAO,
        sentido: "entrada",
        descricao: "Planilha com as abas Servidores, Descontos e Holerite, duas linhas de título antes do cabeçalho",
        arquivos: ARQUIVOS_TESTE_PLANILHA_RETORNO,
        situacao: "teste",
        limitacoes: [
            "O extrato de descontos não traz parcela atual nem total: o cronograma das dívidas herdadas não é atualizado.",
            "Não é atribuível a unidade nenhuma — é um layout sintético, e existe só para a suíte de testes.",
        ],
        camposDerivados: [
            {
                campo: "cpf",
                regra: "Máscara removida, completado com zeros à esquerda até 11 dígitos e conferido pelo dígito verificador.",
            },
            { campo: "matricula", regra: "Sufixo de vínculo (`-A`) removido; o fato entra em `normalizados`." },
            { campo: "competencia", regra: "Mês da coluna `MesReferencia` (AAAA-MM-DD) da aba de servidores." },
            {
                campo: "dataAdmissao",
                regra: "Serial de planilha (dias desde 30/12/1899) convertido para DDMMAAAA.",
            },
        ],
        cenarios: {
            empacotamento: ["planilha-abas", "planilha-por-arquivo"],
            delimitacao: ["celulas-planilha"],
            cabecalho: ["titulos-antes-do-cabecalho", "nome-de-coluna-ordem-livre"],
            "tipo-registro": ["nenhum"],
            "codificacao-fim-de-linha": ["utf-8-bom"],
            numeros: ["decimal-ponto", "separador-de-milhar"],
            "datas-competencia": ["serial-planilha", "aaaa-mm-dd"],
            identificadores: ["cpf-sem-zeros", "cpf-com-mascara", "matricula-com-sufixo"],
            granularidade: ["linha-por-contrato"],
            "campos-ausentes": ["sem-parcela-e-total"],
            "nome-arquivo": ["reconhecido-por-conteudo"],
            sentido: ["entrada"],
            volume: ["volume-real-conhecido"],
        },
        pendencias: [],
        exemplo: {
            descricao: "As três abas com duas linhas de título, CPF sem zeros e com máscara, matrícula com sufixo, data serial e valor com separador de milhar.",
        },
    },
    {
        codigo: "teste-planilha-movimento",
        nome: "Teste — movimento em planilha",
        versao: TESTE_PLANILHA_VERSAO,
        sentido: "saida",
        descricao: "Planilha de uma aba, cabeçalho por nome, uma linha por contrato e a ADF na coluna `Referencia`",
        arquivos: ARQUIVOS_TESTE_PLANILHA_MOVIMENTO,
        situacao: "teste",
        limitacoes: [
            "Não é atribuível a unidade nenhuma — é um layout sintético, e existe só para a suíte de testes.",
        ],
        camposDerivados: [
            { campo: "MesReferencia", regra: "Primeiro dia da competência, em AAAA-MM-DD." },
            {
                campo: "Descricao",
                regra: "Descrição da verba no catálogo de rubricas da unidade; o nome do credor quando não há.",
            },
        ],
        cenarios: {
            empacotamento: ["planilha-abas"],
            delimitacao: ["celulas-planilha"],
            cabecalho: ["nome-de-coluna-ordem-livre"],
            "tipo-registro": ["nenhum"],
            "codificacao-fim-de-linha": ["utf-8"],
            numeros: ["decimal-ponto"],
            "datas-competencia": ["aaaa-mm-dd"],
            identificadores: ["cpf-11-digitos"],
            granularidade: ["linha-por-contrato"],
            "campos-ausentes": ["nenhum"],
            "nome-arquivo": ["padrao-por-competencia", "marca-previa-definitivo"],
            sentido: ["saida"],
            volume: ["volume-real-conhecido"],
        },
        pendencias: [],
        exemplo: {
            descricao: "O movimento de duas dívidas numa planilha de uma aba, com a ADF na coluna Referencia.",
        },
        // Sem divida nenhuma sai a linha de cabecalho e mais nada — arquivo de zero
        // bytes seria indistinguivel de falha de geracao.
        casoVazio: "so-cabecalho",
    },
];
export const LAYOUT_PADRAO = {
    entrada: "atlas-retorno",
    saida: "atlas-movimento",
};
/**
 * Situacao sobrescrita EM TESTE.
 *
 * Nao usa `process.env`: o dominio roda dentro do Worker, onde `NODE_ENV` nao e'
 * "test" e onde depender de `process` e' pedir defeito. O mecanismo e' um mapa
 * que so' o teste popula, e `limparSituacaoDeTeste()` o esvazia — codigo de
 * producao nunca chama nenhum dos dois, e a busca por `situacaoDeTeste` mostra
 * isso em um grep.
 */
const _situacaoDeTeste = new Map();
/** SO EM TESTE. Permite exercitar um layout `rascunho` como se fosse atribuível. */
export function situacaoDeTeste(codigo, situacao) {
    _situacaoDeTeste.set(codigo, situacao);
}
/** SO EM TESTE. Desfaz todas as sobrescritas. */
export function limparSituacaoDeTeste() {
    _situacaoDeTeste.clear();
}
/** A situacao efetiva do layout, considerando sobrescrita de teste. */
export function situacaoDoLayout(def) {
    return _situacaoDeTeste.get(def.codigo) ?? def.situacao;
}
const PORCODIGO = new Map();
for (const def of CATALOGO_LAYOUTS) {
    PORCODIGO.set(def.codigo, def);
    for (const alias of def.aliases ?? [])
        PORCODIGO.set(alias, def);
}
/** Resolve codigo do catalogo OU alias legado gravado antes desta feature. */
export function layoutDoCatalogo(codigoOuAlias) {
    if (!codigoOuAlias)
        return undefined;
    return PORCODIGO.get(codigoOuAlias.trim());
}
/** `true` quando o codigo e' de um layout padrao Atlas. */
export function ehLayoutPadrao(codigo) {
    const def = layoutDoCatalogo(codigo);
    return !!def && def.codigo === LAYOUT_PADRAO[def.sentido];
}
/**
 * Layouts que a averbadora pode atribuir a uma unidade, naquele sentido.
 *
 * `teste` nunca entra, nem com `situacaoDeTeste` — a sobrescrita serve para
 * exercitar RASCUNHO como atribuivel; um sintetico atribuido a unidade real
 * seria um arquivo que nenhum ente le.
 */
export function layoutsAtribuiveis(sentido) {
    return CATALOGO_LAYOUTS.filter((d) => d.sentido === sentido &&
        d.finalidade !== "carga_inicial" &&
        d.finalidade !== "entrada_consignataria" &&
        // O retorno a consignataria nao e' o movimento de uma unidade: e' gerado
        // por credor, a partir do retorno processado. Atribui-lo trancaria a
        // unidade num arquivo que nao e' dela.
        !ehRetornoAConsignataria(d) &&
        d.situacao !== "teste" &&
        situacaoDoLayout(d) === "disponivel");
}
/** `true` para o layout gerado PARA a consignataria (#189): nunca atribuivel, nunca movimento. */
export function ehRetornoAConsignataria(codigoOuDef) {
    const def = typeof codigoOuDef === "string" ? layoutDoCatalogo(codigoOuDef) : codigoOuDef;
    return def?.finalidade === "retorno_consignataria";
}
/**
 * `true` para o arquivo mensal de consignataria (#165, ADR-0013). Ele tem
 * `sentido: "entrada"`, mas NAO e' arquivo da folha de unidade nenhuma: nao se
 * atribui, nao se ensaia como retorno e nao se sugere para o formato de um ente.
 * Todo consumidor de "layouts de entrada" da folha passa por aqui.
 */
export function ehArquivoDeConsignataria(codigoOuDef) {
    const def = typeof codigoOuDef === "string" ? layoutDoCatalogo(codigoOuDef) : codigoOuDef;
    return def?.finalidade === "entrada_consignataria";
}
/** O catalogo como a API o LISTA: tudo menos os layouts de teste (feature 008). */
export function layoutsVisiveis() {
    return CATALOGO_LAYOUTS.filter((d) => d.situacao !== "teste");
}
/** `true` para os sinteticos — o que toda listagem e todo documento publico filtra. */
export function ehLayoutDeTeste(codigoOuDef) {
    const def = typeof codigoOuDef === "string" ? layoutDoCatalogo(codigoOuDef) : codigoOuDef;
    return def?.situacao === "teste";
}
/** Papeis obrigatorios do layout — o que o pacote precisa trazer. */
export function arquivosObrigatorios(def) {
    return def.arquivos.filter((a) => a.obrigatorio);
}
/**
 * Campos que o layout declara obrigatorios e a geracao deixou vazios, com o que
 * corrigir em cada um.
 *
 * O relatorio sempre acusou; ninguem era obrigado a ler, e o movimento da ALECE
 * saiu com FF vazio nas 912 linhas (23/09/2026). Quem EMITE recusa por isto;
 * o ensaio nao, porque e' ele que mostra o que falta antes de emitir. Campo
 * acusado mas opcional no layout (a `referencia` da ALECE) continua so' aviso.
 */
export function camposObrigatoriosFaltantes(def, relatorio) {
    const obrigatorios = new Set(def.arquivos.flatMap((a) => a.colunas.filter((c) => c.obrigatorio).map((c) => c.nome)));
    return relatorio.camposSemCorrespondencia
        .filter((campo) => obrigatorios.has(campo))
        .map((campo) => ({ campo, chaves: relatorio.faltantesPorCampo?.[campo] ?? [] }));
}
/**
 * O catalogo esta consistente consigo mesmo?
 *
 * Roda em teste (gate de commit) e na validacao de envio, pelo mesmo motivo que
 * `validarLayoutColunas` ja rodava: uma edicao errada aqui produziria leitura
 * plausivel e errada, em silencio.
 */
export function validarCatalogo(opts = {}) {
    const provaPadraoPendente = opts.provaPadraoPendente ?? PROVA_PADRAO_PENDENTE;
    const provaAlecePendente = opts.provaAlecePendente ?? PROVA_ALECE_PENDENTE;
    const problemas = [];
    const codigos = new Set();
    const aliases = new Set();
    for (const def of CATALOGO_LAYOUTS) {
        if (codigos.has(def.codigo))
            problemas.push(`codigo repetido: ${def.codigo}`);
        codigos.add(def.codigo);
        // ---- feature 008: cenarios, pendencias, prova, caso vazio, teste
        problemas.push(...validarCenarios(def.cenarios, def.sentido).map((p) => `${def.codigo}: cenarios: ${p}`));
        if (!def.exemplo || def.exemplo.descricao.trim() === "")
            problemas.push(`${def.codigo}: sem descricao de exemplo`);
        if (def.sentido === "saida" && !def.casoVazio)
            problemas.push(`${def.codigo}: saida sem casoVazio declarado`);
        if (def.sentido === "entrada" && def.casoVazio)
            problemas.push(`${def.codigo}: entrada nao tem casoVazio`);
        for (const p of def.pendencias) {
            if (p.descricao.trim() === "" || !/^\d{4}-\d{2}-\d{2}$/.test(p.desde)) {
                problemas.push(`${def.codigo}: pendencia sem descricao ou sem data AAAA-MM-DD`);
            }
        }
        if (def.situacao === "disponivel") {
            if (def.pendencias.length > 0)
                problemas.push(`${def.codigo}: disponivel com ${def.pendencias.length} pendencia(s) aberta(s)`);
            const excecaoPadrao = (provaPadraoPendente && def.codigo.startsWith("atlas-"))
                || (provaAlecePendente && def.codigo.startsWith("alece-"));
            if (!def.provaHomolog && !excecaoPadrao)
                problemas.push(`${def.codigo}: disponivel sem prova em homolog registrada`);
        }
        if (def.situacao === "teste") {
            if (!def.codigo.startsWith("teste-"))
                problemas.push(`${def.codigo}: layout de teste precisa do prefixo "teste-"`);
            if (def.pendencias.length > 0)
                problemas.push(`${def.codigo}: layout de teste nao tem pendencia`);
            if (def.provaHomolog)
                problemas.push(`${def.codigo}: layout de teste nao tem prova em homolog`);
        }
        else if (def.codigo.startsWith("teste-")) {
            problemas.push(`${def.codigo}: prefixo "teste-" so' para situacao "teste"`);
        }
        if (def.versao.trim() === "")
            problemas.push(`${def.codigo}: versao vazia`);
        if (def.arquivos.length === 0 && def.situacao !== "rascunho") {
            problemas.push(`${def.codigo}: sem arquivos declarados`);
        }
        for (const alias of def.aliases ?? []) {
            if (aliases.has(alias))
                problemas.push(`alias repetido: ${alias}`);
            aliases.add(alias);
        }
        const papeis = new Set();
        for (const a of def.arquivos) {
            if (papeis.has(a.papel))
                problemas.push(`${def.codigo}: papel repetido "${a.papel}"`);
            papeis.add(a.papel);
            if (a.obrigatorio && a.reconhecidoPor.trim() === "") {
                problemas.push(`${def.codigo}/${a.papel}: obrigatorio sem "reconhecidoPor"`);
            }
            if (def.sentido === "saida" && a.codificacao === "detectar") {
                problemas.push(`${def.codigo}/${a.papel}: saida nao pode ter codificacao "detectar"`);
            }
            if (def.sentido === "saida" && a.separador === "detectar") {
                problemas.push(`${def.codigo}/${a.papel}: saida nao pode ter separador "detectar"`);
            }
            problemas.push(...validarLayoutColunas(a.colunas).map((p) => `${def.codigo}/${a.papel}: ${p}`));
        }
    }
    for (const alias of aliases) {
        if (codigos.has(alias))
            problemas.push(`alias "${alias}" colide com um codigo do catalogo`);
    }
    for (const sentido of ["entrada", "saida"]) {
        const padrao = CATALOGO_LAYOUTS.find((d) => d.codigo === LAYOUT_PADRAO[sentido]);
        if (!padrao)
            problemas.push(`sentido ${sentido}: layout padrao ausente do catalogo`);
        else if (padrao.sentido !== sentido)
            problemas.push(`${padrao.codigo}: sentido divergente do padrao ${sentido}`);
        else if (padrao.situacao !== "disponivel")
            problemas.push(`${padrao.codigo}: padrao precisa estar disponivel`);
    }
    return problemas;
}
//# sourceMappingURL=catalogo.js.map